import { Link } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { Logo } from "./Logo";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navLinks = [
    { name: "Residential", href: "/services" },
    { name: "Commercial", href: "/commercial" },
    { name: "Service Areas", href: "/areas" },
    { name: "Calculator", href: "/calculator" },
    { name: "Financing", href: "/financing" },
    { name: "Contact", href: "/contact" },
    { name: "About", href: "/about" },
    { name: "FAQ", href: "/faq" },
    { name: "Blog", href: "/blog" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-primary-foreground/10 bg-primary text-primary-foreground">
      <div className="container mx-auto flex h-20 items-center px-4 relative">
        <Link
          to="/"
          className="flex items-center space-x-2 text-primary-foreground hover:opacity-90 transition-opacity mr-8"
          aria-label="Sask Roofing Company Home"
        >
          <Logo className="h-8 md:h-10" aria-hidden="true" />
          <span className="sr-only">Sask Roofing Company Home</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center flex-1">
          <div className="flex flex-1 justify-center gap-6">
            <div className="relative group">
              <Link
                to="/services"
                className="text-sm font-medium text-primary-foreground/80 transition-colors hover:text-primary-foreground flex items-center gap-1"
              >
                Residential
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </Link>
              <div className="absolute top-full left-0 mt-2 w-64 rounded-md border bg-popover p-2 text-popover-foreground shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="grid gap-1">
                  <Link
                    to="/services/repair"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">
                      Roof Repair
                    </div>
                  </Link>
                  <Link
                    to="/services/storm-damage"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">
                      Storm & Hail
                    </div>
                  </Link>
                  <Link
                    to="/services/inspections"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">
                      Roof Inspection
                    </div>
                  </Link>
                  <Link
                    to="/services/replacement"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">
                      Roof Replacement
                    </div>
                  </Link>
                  <Link
                    to="/services/asphalt"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">
                      Asphalt Shingle
                    </div>
                  </Link>
                  <Link
                    to="/services/metal"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">
                      Metal Roofing
                    </div>
                  </Link>
                  <Link
                    to="/services/eavestrough"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">
                      Eavestrough
                    </div>
                  </Link>
                  <Link
                    to="/commercial"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">
                      Commercial
                    </div>
                  </Link>
                  <div className="h-px bg-border my-1 mx-2"></div>
                  <Link
                    to="/services"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-bold leading-none text-primary">
                      See all services
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/commercial"
              className="text-sm font-medium text-primary-foreground/80 transition-colors hover:text-primary-foreground"
            >
              Commercial
            </Link>

            <div className="relative group">
              <Link
                to="/areas"
                className="text-sm font-medium text-primary-foreground/80 transition-colors hover:text-primary-foreground flex items-center gap-1"
              >
                Service Areas
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </Link>
              <div className="absolute top-full left-0 mt-2 w-56 rounded-md border bg-popover p-2 text-popover-foreground shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="grid gap-1">
                  {[
                    "Saskatoon",
                    "Regina",
                    "Moose Jaw",
                    "Prince Albert",
                    "Swift Current",
                    "Yorkton",
                    "Estevan",
                    "Weyburn",
                  ].map((city) => (
                    <Link
                      key={city}
                      to={`/areas/${city.toLowerCase().replace(" ", "-")}`}
                      className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                    >
                      <div className="text-sm font-medium leading-none">
                        {city}
                      </div>
                    </Link>
                  ))}
                  <div className="h-px bg-border my-1 mx-2"></div>
                  <Link
                    to="/areas"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-bold leading-none text-primary">
                      Browse all service areas
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/calculator"
              className="text-sm font-medium text-primary-foreground/80 transition-colors hover:text-primary-foreground"
            >
              Calculator
            </Link>
            <Link
              to="/financing"
              className="text-sm font-medium text-primary-foreground/80 transition-colors hover:text-primary-foreground"
            >
              Financing
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-primary-foreground/80 transition-colors hover:text-primary-foreground"
            >
              Contact
            </Link>

            <div className="relative group">
              <span className="text-sm font-medium text-primary-foreground/80 transition-colors hover:text-primary-foreground flex items-center gap-1 cursor-pointer">
                More
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </span>
              <div className="absolute top-full right-0 mt-2 w-48 rounded-md border bg-popover p-2 text-popover-foreground shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="grid gap-1">
                  <Link
                    to="/about"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">
                      About Us
                    </div>
                  </Link>
                  <Link
                    to="/faq"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">FAQ</div>
                  </Link>
                  <Link
                    to="/blog"
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  >
                    <div className="text-sm font-medium leading-none">Blog</div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Button
              asChild
              variant="outline"
              className="bg-transparent text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/10 hidden lg:inline-flex"
            >
              <a href="tel:6397397969" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <span>(639) 739-7969</span>
              </a>
            </Button>
            <Button
              asChild
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <Link to="/contact">Free Roof Inspection</Link>
            </Button>
          </div>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden p-2 text-primary-foreground ml-auto"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle mobile menu"
        >
          {isMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-primary-foreground/10 bg-primary px-4 py-4 space-y-4">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-lg font-medium text-primary-foreground"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </nav>
          <div className="flex flex-col gap-4 pt-4 border-t border-primary-foreground/10">
            <Button
              asChild
              variant="outline"
              className="w-full bg-transparent text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/10 text-lg py-6"
            >
              <a
                href="tel:6397397969"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center justify-center gap-2"
              >
                <Phone className="h-5 w-5" />
                <span>(639) 739-7969</span>
              </a>
            </Button>
            <Button
              asChild
              className="w-full bg-accent text-accent-foreground hover:bg-accent/90 text-lg py-6"
            >
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                Get Your Free Roof Inspection
              </Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
